import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';

import {
  listHinovaCooperativas,
  listHinovaRegionais,
  listHinovaTiposVeiculo,
  listHinovaVeiculos,
  listHinovaSituacoes,
  createVehicleApi,
  getContractorVehicleTypesApi,
  getVehicleServicesByTypeIDApi,
  loadContractors,
} from '~/services/api';
import { handleApiErrorResponse } from '~/services/handleErrors';
import { HinovaCooperativa, HinovaRegional, HinovaTipoVeiculo, HinovaVeiculo, HinovaSituacao, HinovaFilterPresetFilters } from '~/types';
import { Table } from '~/components/Table';
import InputComponent from '~/components/InputComponent';
import Button from '~/components/Button';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';
import { CustomAutocomplete } from '~/components/CustomAutocomplete';
import FilterPresetManager from './FilterPresetManager';

interface Organization {
  id: string;
  email?: string;
  company_name: string;
  contractor_id?: string;
  social_identity?: string;
}

interface CategoryType {
  id: string;
  description: string;
  subtypes?: CategoryType[];
}

interface ServiceType {
  id: string;
  name?: string;
  description?: string;
  service_description?: string;
  [key: string]: any;
}

interface ImportFilters {
  situacao: string[];
  tipoVeiculo: string[];
  cooperativa: string[];
  regional: string[];
  regiao: string[];
  valorFipeMin: string;
  valorFipeMax: string;
  ano: string;
  dataContratoInicial: string;
  dataContratoFinal: string;
}

const initialFilters: ImportFilters = {
  situacao: [],
  tipoVeiculo: [],
  cooperativa: [],
  regional: [],
  regiao: [],
  valorFipeMin: '',
  valorFipeMax: '',
  ano: '',
  dataContratoInicial: '',
  dataContratoFinal: '',
};

interface BulkProgressItem {
  placa: string;
  status: 'pending' | 'success' | 'error';
  message: string;
}

const firstFilled = (...values: unknown[]) =>
  values.find(value => value !== undefined && value !== null && String(value).trim() !== '');

const parseNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') return value;

  const sanitized = String(value).replace(/[^\d,.-]/g, '');
  const normalized = sanitized.includes(',') ? sanitized.replace(/\./g, '').replace(',', '.') : sanitized;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const isMissingHinovaValue = (value: unknown) => {
  const normalized = String(value ?? '').trim();
  return !normalized || normalized === '-';
};

const cleanVehicleColor = (value: unknown) => {
  if (isMissingHinovaValue(value)) return '';
  return String(value).trim().toUpperCase();
};

const getServiceLabel = (service: ServiceType) =>
  String(
    firstFilled(
      service.description,
      service.name,
      service.service_description,
      service.nome,
      service.descricao,
      service.id,
    ) ?? '',
  );

const getVehicleTypeDescription = (vehicle: HinovaVeiculo, tiposVeiculo: HinovaTipoVeiculo[]) => {
  const codigoTipo = firstFilled(
    vehicle.codigo_tipo_veiculo,
    (vehicle as any).codigo_tipo,
    (vehicle as any).codigo_tipo_veiculo_associado,
    (vehicle as any).tipo_veiculo_codigo,
  );

  const tipoFromList = tiposVeiculo.find(tipo =>
    [codigoTipo, vehicle.tipo_veiculo].some(codigo => String(tipo.codigo_tipo ?? '') === String(codigo ?? '')),
  );

  return firstFilled(
    (vehicle as any).descricao_tipo_veiculo,
    (vehicle as any).descricao_tipo,
    (vehicle as any).tipo_veiculo_descricao,
    (vehicle as any).tipoVeiculo,
    tipoFromList?.descricao_tipo,
    vehicle.tipo_veiculo,
    (vehicle as any).tipo,
  );
};

const normalizeHinovaVehicle = (vehicle: HinovaVeiculo, tiposVeiculo: HinovaTipoVeiculo[]): HinovaVeiculo => {
  const ano = parseNumber(
    firstFilled(
      vehicle.ano,
      vehicle.ano_modelo,
      vehicle.ano_fabricacao,
      (vehicle as any).ano_veiculo,
      (vehicle as any).anoModelo,
      (vehicle as any).anoFabricacao,
    ),
  );
  const valorFipe = parseNumber(firstFilled(vehicle.valor_fipe, (vehicle as any).valorFipe, (vehicle as any).valor_fipe_atual));

  return {
    ...vehicle,
    ano,
    cor: cleanVehicleColor(
      firstFilled(
        vehicle.cor,
        (vehicle as any).cor_veiculo,
        (vehicle as any).descricao_cor,
        (vehicle as any).nome_cor,
        (vehicle as any).corVeiculo,
      ),
    ),
    tipo_veiculo: String(getVehicleTypeDescription(vehicle, tiposVeiculo) ?? ''),
    valor_fipe: valorFipe,
  };
};

export default function Importacao() {
  const [filters, setFilters] = useState<ImportFilters>(initialFilters);
  const [loading, setLoading] = useState(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [cooperativas, setCooperativas] = useState<HinovaCooperativa[]>([]);
  const [regionais, setRegionais] = useState<HinovaRegional[]>([]);
  const [tiposVeiculo, setTiposVeiculo] = useState<HinovaTipoVeiculo[]>([]);
  const [situacoes, setSituacoes] = useState<HinovaSituacao[]>([]);

  const [vehicles, setVehicles] = useState<HinovaVeiculo[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<HinovaVeiculo[]>([]);
  const [showTable, setShowTable] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const [openSingleImportModal, setOpenSingleImportModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<HinovaVeiculo | null>(null);
  const [singleImportLoading, setSingleImportLoading] = useState(false);

  // Importação em massa
  const [openBulkConfigModal, setOpenBulkConfigModal] = useState(false);
  const [openBulkProgressModal, setOpenBulkProgressModal] = useState(false);
  const [bulkImportRunning, setBulkImportRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<BulkProgressItem[]>([]);
  const bulkAbortRef = React.useRef(false);

  // Modal fields
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [contractorCategoryTypes, setContractorCategoryTypes] = useState<CategoryType[]>([]);
  const [selectedCategoryTypeId, setSelectedCategoryTypeId] = useState('');
  const [selectedSubCategoryTypeId, setSelectedSubCategoryTypeId] = useState('');
  const [selectedCategoryType, setSelectedCategoryType] = useState<CategoryType | null>(null);
  const [servicesType, setServicesType] = useState<ServiceType[]>([]);
  const [selectedServicesType, setSelectedServicesType] = useState<Record<string, boolean>>({});
  const [importFormData, setImportFormData] = useState({
    organization_id: '',
    color: '',
    city: '',
    state: '',
  });

  const loadFilters = useCallback(async () => {
    setLoading(true);
    const failedSources: string[] = [];
    let lastError: ReturnType<typeof handleApiErrorResponse> | null = null;

    const setDataFromResponse = (response: any, setter: (data: any[]) => void) => {
      if (response?.status === 200 && response?.data != null) {
        const data = response.data;
        setter(Array.isArray(data) ? data : data.data || []);
        return true;
      }
      return false;
    };

    try {
      const [cooperativasResponse, regionaisResponse, tiposVeiculoResponse, situacoesResponse] = await Promise.allSettled([
        listHinovaCooperativas(),
        listHinovaRegionais(),
        listHinovaTiposVeiculo('ativo'),
        listHinovaSituacoes(),
      ]);

      if (cooperativasResponse.status === 'fulfilled') {
        if (!setDataFromResponse(cooperativasResponse.value, setCooperativas)) {
          failedSources.push('Cooperativas');
        }
      } else {
        failedSources.push('Cooperativas');
        lastError = handleApiErrorResponse(cooperativasResponse.reason);
      }

      if (regionaisResponse.status === 'fulfilled') {
        if (!setDataFromResponse(regionaisResponse.value, setRegionais)) {
          failedSources.push('Regionais');
        }
      } else {
        failedSources.push('Regionais');
        if (!lastError) lastError = handleApiErrorResponse(regionaisResponse.reason);
      }

      if (tiposVeiculoResponse.status === 'fulfilled') {
        if (!setDataFromResponse(tiposVeiculoResponse.value, setTiposVeiculo)) {
          failedSources.push('Tipos de Veículo');
        }
      } else {
        failedSources.push('Tipos de Veículo');
        if (!lastError) lastError = handleApiErrorResponse(tiposVeiculoResponse.reason);
      }

      if (situacoesResponse.status === 'fulfilled') {
        setDataFromResponse(situacoesResponse.value, setSituacoes);
      }

      if (failedSources.length > 0) {
        const detail =
          failedSources.length === 3
            ? 'Não foi possível carregar filtros de importação.'
            : `Não foi possível carregar: ${failedSources.join(', ')}.`;
        setSeverity('warning');
        setMessageSnack(
          (lastError?.applicationErrorMessage || lastError?.apiError || 'Ocorreu um erro') + ': ' + detail,
        );
        setOpenSnackbar(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setFilters(initialFilters);
    setShowTable(false);
    setFilteredVehicles([]);
    setVehicles([]);
    loadFilters();
  }, [loadFilters]);

  const loadOrganizations = useCallback(async () => {
    try {
      setLoadingOrganizations(true);
      const response = await loadContractors();
      if (response.status === 200 && Array.isArray(response.data)) {
        const organizationsMap = new Map<string, Organization>();

        response.data.forEach(contractor => {
          if (!contractor.organization_id) return;

          organizationsMap.set(contractor.organization_id, {
            id: contractor.organization_id,
            contractor_id: contractor.id,
            company_name: contractor.name || contractor.organization || contractor.organization_id,
          });
        });

        setOrganizations(Array.from(organizationsMap.values()));
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível carregar organizações.',
      );
      setOpenSnackbar(true);
    } finally {
      setLoadingOrganizations(false);
    }
  }, []);

  const loadContractorVehicleTypes = useCallback(async () => {
    try {
      const response = await getContractorVehicleTypesApi();
      if (response.data) {
        setContractorCategoryTypes(response.data);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível carregar tipos de veículo.',
      );
      setOpenSnackbar(true);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
    loadContractorVehicleTypes();
  }, [loadOrganizations, loadContractorVehicleTypes]);

  useEffect(() => {
    const typeId = selectedSubCategoryTypeId || selectedCategoryTypeId;
    if (!typeId) {
      setServicesType([]);
      setSelectedServicesType({});
      return;
    }
    getVehicleServicesByTypeIDApi(typeId)
      .then(response => {
        setServicesType(response.data || []);
        setSelectedServicesType({});
      })
      .catch(() => {
        setServicesType([]);
      });
  }, [selectedCategoryTypeId, selectedSubCategoryTypeId]);

  const parseHinovaDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    if (dateStr.includes('/')) {
      const [d, m, y] = dateStr.split('/');
      const parsed = new Date(`${y}-${m}-${d}`);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleFilter = useCallback(async () => {
    if (!filters.situacao || filters.situacao.length === 0) {
      setSeverity('warning');
      setMessageSnack('Selecione pelo menos uma situação antes de filtrar.');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoadingVehicles(true);

      const responses = await Promise.all(
        filters.situacao.map(situacaoCode => listHinovaVeiculos(situacaoCode)),
      );

      const mergedMap = new Map<string, HinovaVeiculo>();
      for (const response of responses) {
        if (response.status === 200 && response.data) {
          const data = response.data;
          const batch: HinovaVeiculo[] = Array.isArray(data) ? data : data.veiculos || [];
          for (const vehicle of batch) {
            const normalizedVehicle = normalizeHinovaVehicle(vehicle, tiposVeiculo);
            const key = normalizedVehicle.placa || normalizedVehicle.chassi || JSON.stringify(normalizedVehicle);
            if (!mergedMap.has(key)) {
              mergedMap.set(key, normalizedVehicle);
            }
          }
        }
      }

      const allVehicles = Array.from(mergedMap.values());
      setVehicles(allVehicles);

      const dataInicialDate = filters.dataContratoInicial ? new Date(filters.dataContratoInicial) : null;
      const dataFinalDate = filters.dataContratoFinal ? new Date(filters.dataContratoFinal) : null;

      const filtered = allVehicles.filter((vehicle: HinovaVeiculo) => {
        if (filters.tipoVeiculo.length > 0) {
          const vehicleCodigoTipo =
            (vehicle as any).codigo_tipo_veiculo?.toString() ||
            (vehicle as any).codigo_tipo?.toString() ||
            '';
          if (!filters.tipoVeiculo.includes(vehicleCodigoTipo)) {
            return false;
          }
        }
        if (filters.ano) {
          const anoFilter = Number(filters.ano);
          const vehicleAno = (vehicle as any).ano_fabricacao || (vehicle as any).ano_modelo || vehicle.ano;
          if (Number(vehicleAno) !== anoFilter) {
            return false;
          }
        }
        if ((filters.valorFipeMin || filters.valorFipeMax) && vehicle.valor_fipe != null) {
          if (filters.valorFipeMin && vehicle.valor_fipe < Number(filters.valorFipeMin)) {
            return false;
          }
          if (filters.valorFipeMax && vehicle.valor_fipe > Number(filters.valorFipeMax)) {
            return false;
          }
        }
        if (filters.cooperativa.length > 0) {
          const vehicleCooperativa =
            (vehicle as any).codigo_cooperativa?.toString() || vehicle.cooperativa?.toString() || '';
          const validCooperativas = filters.cooperativa.filter(c => !c.startsWith('coop-'));
          if (validCooperativas.length > 0 && !validCooperativas.includes(vehicleCooperativa)) {
            return false;
          }
        }
        if (filters.regional.length > 0) {
          const vehicleRegional =
            (vehicle as any).codigo_regional?.toString() || vehicle.regional?.toString() || '';
          const validRegionais = filters.regional.filter(r => !r.startsWith('regional-'));
          if (validRegionais.length > 0 && !validRegionais.includes(vehicleRegional)) {
            return false;
          }
        }
        if (filters.regiao.length > 0) {
          const vehicleRegiao = vehicle.regiao || (vehicle as any).nome_regiao || '';
          if (vehicleRegiao && !filters.regiao.includes(vehicleRegiao)) {
            return false;
          }
        }
        if (dataInicialDate || dataFinalDate) {
          const rawDataContrato =
            (vehicle as any).data_contrato || (vehicle as any).data_vigencia || '';
          // Comparar apenas YYYY-MM-DD para evitar problemas de fuso horário.
          // A Hinova retorna "2026-05-04T00:00:00-0300" que em UTC vira 03:00 do mesmo dia,
          // fazendo a comparação com Date falhar quando o filtro final é exatamente esse dia.
          const vehicleDateStr = rawDataContrato ? rawDataContrato.substring(0, 10) : null;
          const inicialStr = filters.dataContratoInicial || null;
          const finalStr = filters.dataContratoFinal || null;
          if (!vehicleDateStr) return false;
          if (inicialStr && vehicleDateStr < inicialStr) return false;
          if (finalStr && vehicleDateStr > finalStr) return false;
        }
        return true;
      });

      setFilteredVehicles(filtered);
      setShowTable(true);
      setSeverity('success');
      setMessageSnack(`${filtered.length} veículo(s) encontrado(s).`);
      setOpenSnackbar(true);
    } catch (err) {
      const error = handleApiErrorResponse(err);
      const statusCode =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      const is404 = statusCode === 404;

      setSeverity('warning');
      if (is404) {
        setMessageSnack(
          'O endpoint para listar veículos não está disponível na API Hinova. A funcionalidade de filtragem requer um endpoint que retorne a lista completa de veículos.',
        );
      } else {
        setMessageSnack(
          (error?.applicationErrorMessage || error?.apiError || 'Erro desconhecido') +
            ': não foi possível pesquisar veículos. Tente novamente.',
        );
      }
      setOpenSnackbar(true);
      setShowTable(false);
      setFilteredVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  }, [filters, tiposVeiculo]);

  const handleLoadPreset = useCallback((presetFilters: HinovaFilterPresetFilters) => {
    setFilters(presetFilters);
    setSeverity('info');
    setMessageSnack('Filtro carregado. Ajuste se necessário e clique em Filtrar.');
    setOpenSnackbar(true);
  }, []);

  const hasAtLeastOneFilter = useMemo(() => {
    return (
      filters.situacao.length > 0 ||
      filters.tipoVeiculo.length > 0 ||
      filters.cooperativa.length > 0 ||
      filters.regional.length > 0 ||
      !!filters.valorFipeMin ||
      !!filters.valorFipeMax ||
      !!filters.ano ||
      filters.regiao.length > 0 ||
      !!filters.dataContratoInicial ||
      !!filters.dataContratoFinal
    );
  }, [filters]);

  const handleOpenSingleImportModal = useCallback((vehicle: HinovaVeiculo) => {
    setSelectedVehicle(vehicle);
    setImportFormData({ organization_id: '', color: cleanVehicleColor(vehicle.cor), city: '', state: '' });
    setSelectedCategoryTypeId('');
    setSelectedSubCategoryTypeId('');
    setSelectedCategoryType(null);
    setServicesType([]);
    setSelectedServicesType({});
    setOpenSingleImportModal(true);
  }, []);

  const handleCloseSingleImportModal = useCallback(() => {
    setOpenSingleImportModal(false);
    setSelectedVehicle(null);
  }, []);

  const handleChangeType = useCallback((value: string) => {
    const founded =
      contractorCategoryTypes.find(item => item.id === value) ??
      contractorCategoryTypes
        .filter(item => !!item.subtypes)
        .find(item => item.subtypes?.find(sub => sub.id === value));
    const subtypeFounded = founded?.subtypes?.find(sub => sub.id === value);

    setSelectedCategoryTypeId(founded?.id ?? '');
    setSelectedSubCategoryTypeId(subtypeFounded?.id ?? '');
    setSelectedCategoryType(founded ?? null);
  }, [contractorCategoryTypes]);

  const handleSingleImport = useCallback(async () => {
    if (!selectedVehicle) return;

    if (!importFormData.organization_id) {
      setSeverity('warning');
      setMessageSnack('Selecione uma organização.');
      setOpenSnackbar(true);
      return;
    }
    if (!selectedCategoryTypeId) {
      setSeverity('warning');
      setMessageSnack('Selecione o tipo de veículo.');
      setOpenSnackbar(true);
      return;
    }
    if (!importFormData.color.trim()) {
      setSeverity('warning');
      setMessageSnack('Preencha a cor do veículo.');
      setOpenSnackbar(true);
      return;
    }
    if (!importFormData.city.trim() || !importFormData.state.trim()) {
      setSeverity('warning');
      setMessageSnack('Preencha cidade e estado.');
      setOpenSnackbar(true);
      return;
    }

    const services = Object.entries(selectedServicesType)
      .filter(([, checked]) => checked)
      .map(([service_id]) => ({ service_id }));

    try {
      setSingleImportLoading(true);
      const vehicleData = {
        license_plate: selectedVehicle.placa,
        model: selectedVehicle.modelo || '',
        manufacture: selectedVehicle.marca || '',
        color: importFormData.color.trim().toUpperCase(),
        vin: selectedVehicle.chassi || '',
        model_year: selectedVehicle.ano ? String(selectedVehicle.ano) : '',
        status: 'ATIVO',
        organization_id: importFormData.organization_id,
        city: importFormData.city.toUpperCase(),
        state: importFormData.state.toUpperCase(),
        vehicle_contractor_type_id: selectedSubCategoryTypeId || selectedCategoryTypeId,
        services,
      };
      const response = await createVehicleApi(vehicleData);

      if (response.status === 200 || response.status === 201) {
        setSeverity('success');
        setMessageSnack(`Veículo ${selectedVehicle.placa} importado com sucesso.`);
        setOpenSnackbar(true);
        handleCloseSingleImportModal();
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('error');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError || 'Erro desconhecido') +
          ': não foi possível importar o veículo. Tente novamente.',
      );
      setOpenSnackbar(true);
    } finally {
      setSingleImportLoading(false);
    }
  }, [selectedVehicle, importFormData, selectedCategoryTypeId, selectedSubCategoryTypeId, selectedServicesType, handleCloseSingleImportModal]);

  const handleOpenBulkConfigModal = useCallback(() => {
    if (filteredVehicles.length === 0) {
      setSeverity('warning');
      setMessageSnack('Filtre os veículos antes de iniciar a importação em massa.');
      setOpenSnackbar(true);
      return;
    }
    setImportFormData({ organization_id: '', color: '', city: '', state: '' });
    setSelectedCategoryTypeId('');
    setSelectedSubCategoryTypeId('');
    setSelectedCategoryType(null);
    setServicesType([]);
    setSelectedServicesType({});
    setOpenBulkConfigModal(true);
  }, [filteredVehicles]);

  const handleStartBulkImport = useCallback(async () => {
    if (!importFormData.organization_id) {
      setSeverity('warning');
      setMessageSnack('Selecione uma organização.');
      setOpenSnackbar(true);
      return;
    }
    if (!selectedCategoryTypeId) {
      setSeverity('warning');
      setMessageSnack('Selecione o tipo de veículo.');
      setOpenSnackbar(true);
      return;
    }
    if (!importFormData.city.trim() || !importFormData.state.trim()) {
      setSeverity('warning');
      setMessageSnack('Preencha cidade e estado.');
      setOpenSnackbar(true);
      return;
    }

    const services = Object.entries(selectedServicesType)
      .filter(([, checked]) => checked)
      .map(([service_id]) => ({ service_id }));

    setOpenBulkConfigModal(false);
    bulkAbortRef.current = false;
    setBulkProgress(filteredVehicles.map(v => ({ placa: v.placa || '-', status: 'pending' as const, message: 'Aguardando...' })));
    setBulkImportRunning(true);
    setOpenBulkProgressModal(true);

    for (let i = 0; i < filteredVehicles.length; i++) {
      if (bulkAbortRef.current) {
        setBulkProgress(prev => prev.map((item, idx) => idx >= i ? { ...item, status: 'error' as const, message: 'Cancelado.' } : item));
        break;
      }

      const vehicle = filteredVehicles[i];
      const vehicleColor = importFormData.color.trim()
        ? importFormData.color.trim().toUpperCase()
        : cleanVehicleColor(vehicle.cor);

      setBulkProgress(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'pending' as const, message: 'Importando...' } : item));

      try {
        const vehicleData = {
          license_plate: vehicle.placa,
          model: vehicle.modelo || '',
          manufacture: vehicle.marca || '',
          color: vehicleColor,
          vin: vehicle.chassi || '',
          model_year: vehicle.ano ? String(vehicle.ano) : '',
          status: 'ATIVO',
          organization_id: importFormData.organization_id,
          city: importFormData.city.toUpperCase(),
          state: importFormData.state.toUpperCase(),
          vehicle_contractor_type_id: selectedSubCategoryTypeId || selectedCategoryTypeId,
          services,
        };
        const response = await createVehicleApi(vehicleData);
        if (response.status === 200 || response.status === 201) {
          setBulkProgress(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'success' as const, message: 'Importado com sucesso.' } : item));
        } else {
          setBulkProgress(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'error' as const, message: `Status inesperado: ${response.status}` } : item));
        }
      } catch (err) {
        const error = handleApiErrorResponse(err);
        const msg = error?.applicationErrorMessage || error?.apiError || 'Erro desconhecido';
        setBulkProgress(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'error' as const, message: msg } : item));
      }
    }

    setBulkImportRunning(false);
  }, [filteredVehicles, importFormData, selectedCategoryTypeId, selectedSubCategoryTypeId, selectedServicesType]);

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: 20 }} alignItems='flex-end'>
        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            multiple
            options={situacoes}
            getOptionLabel={(option) => {
              const s = option as HinovaSituacao;
              const codigo = s.codigo_situacao?.toString() || s.codigo?.toString() || '';
              const descricao = s.descricao_situacao || s.descricao || s.situacao || codigo;
              return descricao;
            }}
            getOptionSelected={(option, value) => {
              const codigoOpt = (option as HinovaSituacao).codigo_situacao?.toString() || (option as HinovaSituacao).codigo?.toString() || '';
              const codigoVal = (value as HinovaSituacao).codigo_situacao?.toString() || (value as HinovaSituacao).codigo?.toString() || '';
              return codigoOpt === codigoVal;
            }}
            value={situacoes.filter(s => {
              const codigo = s.codigo_situacao?.toString() || s.codigo?.toString() || '';
              return filters.situacao.includes(codigo);
            })}
            onChange={(_, newValue) => {
              const codigos = (newValue as HinovaSituacao[]).map(s => s.codigo_situacao?.toString() || s.codigo?.toString() || '');
              setFilters(prev => ({ ...prev, situacao: codigos }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Situação *" required variant="outlined" />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            multiple
            options={tiposVeiculo}
            getOptionLabel={(option) => (option as HinovaTipoVeiculo).descricao_tipo || ''}
            getOptionSelected={(option, value) =>
              (option as HinovaTipoVeiculo).codigo_tipo === (value as HinovaTipoVeiculo).codigo_tipo
            }
            value={tiposVeiculo.filter(t => filters.tipoVeiculo.includes(String(t.codigo_tipo ?? '')))}
            onChange={(_, newValue) => {
              const codigos = (newValue as HinovaTipoVeiculo[]).map(t => String(t.codigo_tipo ?? ''));
              setFilters(prev => ({ ...prev, tipoVeiculo: codigos }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Tipo de veículo" variant="outlined" />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <CustomAutocomplete
            multiple
            options={cooperativas}
            getOptionLabel={(option) => (option as HinovaCooperativa).nome ?? (option as any).name ?? ''}
            getOptionSelected={(option, value) => {
              const getKey = (coop: HinovaCooperativa, idx: number) => {
                const codigo = coop.codigo ?? coop.id ?? (coop as any).codigo_cooperativa;
                return codigo != null && codigo !== '' ? String(codigo) : `coop-${idx}`;
              };
              const idxO = cooperativas.indexOf(option as HinovaCooperativa);
              const idxV = cooperativas.indexOf(value as HinovaCooperativa);
              return getKey(option as HinovaCooperativa, idxO) === getKey(value as HinovaCooperativa, idxV);
            }}
            value={cooperativas.filter((c, idx) => {
              const codigo = c.codigo ?? c.id ?? (c as any).codigo_cooperativa;
              const key = codigo != null && codigo !== '' ? String(codigo) : `coop-${idx}`;
              return filters.cooperativa.includes(key);
            })}
            onChange={(_, newValue) => {
              const keys = (newValue as HinovaCooperativa[]).map(c => {
                const codigo = c.codigo ?? c.id ?? (c as any).codigo_cooperativa;
                const idx = cooperativas.indexOf(c);
                return codigo != null && codigo !== '' ? String(codigo) : (idx >= 0 ? `coop-${idx}` : '');
              }).filter(Boolean);
              setFilters(prev => ({ ...prev, cooperativa: keys }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Cooperativa" variant="outlined" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomAutocomplete
            multiple
            options={regionais}
            getOptionLabel={(option) => (option as HinovaRegional).nome ?? (option as any).name ?? ''}
            getOptionSelected={(option, value) => {
              const getKey = (reg: HinovaRegional, idx: number) => {
                const codigo = reg.codigo ?? reg.id ?? (reg as any).codigo_regional;
                return codigo != null && codigo !== '' ? String(codigo) : `regional-${idx}`;
              };
              const idxO = regionais.indexOf(option as HinovaRegional);
              const idxV = regionais.indexOf(value as HinovaRegional);
              return getKey(option as HinovaRegional, idxO) === getKey(value as HinovaRegional, idxV);
            }}
            value={regionais.filter((r, idx) => {
              const codigo = r.codigo ?? r.id ?? (r as any).codigo_regional;
              const key = codigo != null && codigo !== '' ? String(codigo) : `regional-${idx}`;
              return filters.regional.includes(key);
            })}
            onChange={(_, newValue) => {
              const keys = (newValue as HinovaRegional[]).map(reg => {
                const codigo = reg.codigo ?? reg.id ?? (reg as any).codigo_regional;
                const idx = regionais.indexOf(reg);
                return codigo != null && codigo !== '' ? String(codigo) : (idx >= 0 ? `regional-${idx}` : '');
              }).filter(Boolean);
              setFilters(prev => ({ ...prev, regional: keys }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Regional" variant="outlined" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <InputComponent
            label='Valor Fipe mínimo'
            name='valorFipeMin'
            type='number'
            value={filters.valorFipeMin}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, valorFipeMin: value }));
            }}
            error={null}
            placeholder='Ex: 10000'
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <InputComponent
            label='Valor Fipe máximo'
            name='valorFipeMax'
            type='number'
            value={filters.valorFipeMax}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, valorFipeMax: value }));
            }}
            error={null}
            placeholder='Ex: 50000'
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InputComponent
            label='Ano'
            name='ano'
            type='number'
            value={filters.ano}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, ano: value }));
            }}
            error={null}
            placeholder='Ano'
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <CustomAutocomplete
            multiple
            options={regionais}
            getOptionLabel={(option) => (option as HinovaRegional).nome ?? (option as any).name ?? ''}
            getOptionSelected={(option, value) => {
              const nomeO = (option as HinovaRegional).nome ?? (option as any).name ?? '';
              const nomeV = (value as HinovaRegional).nome ?? (value as any).name ?? '';
              return nomeO === nomeV;
            }}
            value={regionais.filter(r => {
              const nome = r.nome ?? (r as any).name ?? '';
              return nome ? filters.regiao.includes(nome) : false;
            })}
            onChange={(_, newValue) => {
              const nomes = (newValue as HinovaRegional[]).map(r => r.nome ?? (r as any).name ?? '').filter(Boolean);
              setFilters(prev => ({ ...prev, regiao: nomes }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Região" variant="outlined" fullWidth />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InputComponent
            label='Data contrato inicial'
            name='dataContratoInicial'
            type='date'
            value={filters.dataContratoInicial}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, dataContratoInicial: value }));
            }}
            error={null}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <InputComponent
            label='Data contrato final'
            name='dataContratoFinal'
            type='date'
            value={filters.dataContratoFinal}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, dataContratoFinal: value }));
            }}
            error={null}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 12,
              width: '100%',
            }}
          >
            <Button
              label='Filtrar'
              loading={loadingVehicles}
              onClick={handleFilter}
              filled
              style={{ flex: 1, minWidth: 0, marginTop: 0 }}
            />
            <FilterPresetManager
              currentFilters={filters as HinovaFilterPresetFilters}
              onLoadPreset={handleLoadPreset}
              onSnack={(message, sev) => {
                setMessageSnack(message);
                setSeverity(sev);
                setOpenSnackbar(true);
              }}
              hasAtLeastOneFilter={hasAtLeastOneFilter}
              buttonStyle={{ flex: 1, minWidth: 0, marginTop: 0 }}
            />
            <Button
              label="Limpar filtros"
              onClick={() => setFilters(initialFilters)}
              filled
              style={{ flex: 1, minWidth: 0, marginTop: 0 }}
            />
          </div>
        </Grid>
      </Grid>

      {showTable && (
        <Grid container spacing={2} style={{ marginTop: 20, marginBottom: 20 }}>
          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              label={`Importação em massa (${filteredVehicles.length})`}
              onClick={handleOpenBulkConfigModal}
              filled
              style={{ backgroundColor: '#6C3FC5', color: '#fff' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Table<HinovaVeiculo>
              isLoading={loadingVehicles}
              data={filteredVehicles}
              columns={{
                placa: { title: 'Placa', width: 100 },
                modelo: { title: 'Modelo', width: 150 },
                marca: { title: 'Marca', width: 120 },
                ano: { title: 'Ano', width: 80, type: 'numeric' },
                cor: { title: 'Cor', width: 100 },
                tipo_veiculo: { title: 'Tipo', width: 120 },
                valor_fipe: {
                  title: 'Valor FIPE',
                  width: 120,
                  render: rowData => {
                    if (!rowData.valor_fipe) return '-';
                    return new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(rowData.valor_fipe);
                  },
                },
                chassi: { title: 'Chassi', width: 150 },
                renavam: { title: 'RENAVAM', width: 120 },
                acoes: {
                  title: 'Ações',
                  width: 150,
                  render: rowData => (
                    <Button
                      label='Importação única'
                      onClick={() => handleOpenSingleImportModal(rowData)}
                      filled
                      style={{ minWidth: 140, backgroundColor: '#00F0FF' }}
                    />
                  ),
                },
              }}
              localization={{
                body: {
                  emptyDataSourceMessage: 'Nenhum veículo encontrado com os filtros selecionados.',
                },
                pagination: {
                  labelRowsSelect: 'veículos',
                },
              }}
              options={{
                showTitle: false,
                pageSize: 10,
                pageSizeOptions: [10, 20, 50],
                paging: true,
                filtering: false,
                search: false,
                toolbar: true,
                exportButton: true,
                exportAllData: true,
              }}
            />
          </Grid>
        </Grid>
      )}

      <Dialog open={openSingleImportModal} onClose={handleCloseSingleImportModal} maxWidth='sm' fullWidth>
        <DialogTitle>
          Importação única — {selectedVehicle?.placa ?? 'Veículo'}
        </DialogTitle>
        <DialogContent>
          {selectedVehicle && (
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12}>
                <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#666' }}>
                  <strong>Modelo:</strong> {selectedVehicle.modelo || '-'} &nbsp;|&nbsp;
                  <strong>Marca:</strong> {selectedVehicle.marca || '-'} &nbsp;|&nbsp;
                  <strong>Ano:</strong> {selectedVehicle.ano || '-'} &nbsp;|&nbsp;
                  <strong>Cor:</strong> {selectedVehicle.cor || '-'} &nbsp;|&nbsp;
                  <strong>Chassi:</strong> {selectedVehicle.chassi || '-'}
                </p>
              </Grid>

              <Grid item xs={12}>
                <CustomAutocomplete
                  options={organizations}
                  getOptionLabel={option => {
                    const org = option as Organization;
                    return org.company_name || org.email || org.id;
                  }}
                  getOptionSelected={(option, value) => (option as Organization).id === (value as Organization).id}
                  value={organizations.find(org => org.id === importFormData.organization_id) || null}
                  onChange={(_, value) => {
                    const org = value as Organization | null;
                    setImportFormData(prev => ({ ...prev, organization_id: org?.id || '' }));
                  }}
                  loading={loadingOrganizations}
                  disabled={loadingOrganizations}
                  renderInput={params => (
                    <TextField {...params} label='Organização *' required fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de veículo *</InputLabel>
                  <Select
                    value={selectedSubCategoryTypeId || selectedCategoryTypeId}
                    onChange={e => handleChangeType(e.target.value as string)}
                  >
                    {contractorCategoryTypes.map(type => [
                      <MenuItem key={type.id} value={type.id}>
                        {type.description}
                      </MenuItem>,
                      ...(type.subtypes?.map(sub => (
                        <MenuItem key={sub.id} value={sub.id} style={{ paddingLeft: 32 }}>
                          {sub.description}
                        </MenuItem>
                      )) ?? []),
                    ])}
                  </Select>
                </FormControl>
              </Grid>

              {servicesType.length > 0 && (
                <Grid item xs={12}>
                  <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600 }}>Serviços</p>
                  <FormGroup row>
                    {servicesType.map(service => (
                      <FormControlLabel
                        key={service.id}
                        control={
                          <Checkbox
                            checked={!!selectedServicesType[service.id]}
                            onChange={e =>
                              setSelectedServicesType(prev => ({ ...prev, [service.id]: e.target.checked }))
                            }
                            value={service.id}
                          />
                        }
                        label={getServiceLabel(service)}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Cor *'
                  value={importFormData.color}
                  onChange={e => setImportFormData(prev => ({ ...prev, color: e.target.value.toUpperCase() }))}
                  required
                />
              </Grid>

              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label='Cidade *'
                  value={importFormData.city}
                  onChange={e => setImportFormData(prev => ({ ...prev, city: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label='Estado *'
                  value={importFormData.state}
                  onChange={e => setImportFormData(prev => ({ ...prev, state: e.target.value }))}
                  inputProps={{ maxLength: 2 }}
                  required
                  placeholder='SP'
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button label='Cancelar' onClick={handleCloseSingleImportModal} disabled={singleImportLoading} />
          <Button
            label='Confirmar importação'
            filled
            loading={singleImportLoading}
            onClick={handleSingleImport}
          />
        </DialogActions>
      </Dialog>

      {/* Modal de configuração — importação em massa */}
      <Dialog open={openBulkConfigModal} onClose={() => setOpenBulkConfigModal(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Importação em massa — {filteredVehicles.length} veículo(s)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <CustomAutocomplete
                options={organizations}
                getOptionLabel={option => {
                  const org = option as Organization;
                  return org.company_name || org.email || org.id;
                }}
                getOptionSelected={(option, value) => (option as Organization).id === (value as Organization).id}
                value={organizations.find(org => org.id === importFormData.organization_id) || null}
                onChange={(_, value) => {
                  const org = value as Organization | null;
                  setImportFormData(prev => ({ ...prev, organization_id: org?.id || '' }));
                }}
                loading={loadingOrganizations}
                disabled={loadingOrganizations}
                renderInput={params => (
                  <TextField {...params} label='Organização *' required fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de veículo *</InputLabel>
                <Select
                  value={selectedSubCategoryTypeId || selectedCategoryTypeId}
                  onChange={e => handleChangeType(e.target.value as string)}
                >
                  {contractorCategoryTypes.map(type => [
                    <MenuItem key={type.id} value={type.id}>
                      {type.description}
                    </MenuItem>,
                    ...(type.subtypes?.map(sub => (
                      <MenuItem key={sub.id} value={sub.id} style={{ paddingLeft: 32 }}>
                        {sub.description}
                      </MenuItem>
                    )) ?? []),
                  ])}
                </Select>
              </FormControl>
            </Grid>

            {servicesType.length > 0 && (
              <Grid item xs={12}>
                <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600 }}>Serviços</p>
                <FormGroup row>
                  {servicesType.map(service => (
                    <FormControlLabel
                      key={service.id}
                      control={
                        <Checkbox
                          checked={!!selectedServicesType[service.id]}
                          onChange={e =>
                            setSelectedServicesType(prev => ({ ...prev, [service.id]: e.target.checked }))
                          }
                          value={service.id}
                        />
                      }
                      label={getServiceLabel(service)}
                    />
                  ))}
                </FormGroup>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Cor (deixe em branco para usar a cor de cada veículo)'
                value={importFormData.color}
                onChange={e => setImportFormData(prev => ({ ...prev, color: e.target.value.toUpperCase() }))}
              />
            </Grid>

            <Grid item xs={8}>
              <TextField
                fullWidth
                label='Cidade *'
                value={importFormData.city}
                onChange={e => setImportFormData(prev => ({ ...prev, city: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label='Estado *'
                value={importFormData.state}
                onChange={e => setImportFormData(prev => ({ ...prev, state: e.target.value }))}
                inputProps={{ maxLength: 2 }}
                required
                placeholder='SP'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button label='Cancelar' onClick={() => setOpenBulkConfigModal(false)} />
          <Button
            label={`Iniciar importação (${filteredVehicles.length})`}
            filled
            style={{ backgroundColor: '#6C3FC5', color: '#fff' }}
            onClick={handleStartBulkImport}
          />
        </DialogActions>
      </Dialog>

      {/* Modal de progresso — importação em massa */}
      <Dialog open={openBulkProgressModal} onClose={() => { if (!bulkImportRunning) setOpenBulkProgressModal(false); }} maxWidth='sm' fullWidth>
        <DialogTitle>
          Progresso da importação em massa
          {bulkImportRunning && (
            <span style={{ fontSize: 13, marginLeft: 12, color: '#888' }}>
              {bulkProgress.filter(p => p.status === 'success').length} / {bulkProgress.length} concluídos
            </span>
          )}
        </DialogTitle>
        <DialogContent style={{ padding: '8px 24px' }}>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {bulkProgress.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <span style={{ fontWeight: 600, minWidth: 90 }}>{item.placa}</span>
                <span
                  style={{
                    color: item.status === 'success' ? '#2e7d32' : item.status === 'error' ? '#c62828' : '#888',
                    fontSize: 13,
                    flex: 1,
                  }}
                >
                  {item.status === 'success' ? '✔' : item.status === 'error' ? '✖' : '⏳'} {item.message}
                </span>
              </div>
            ))}
          </div>
          {!bulkImportRunning && bulkProgress.length > 0 && (
            <p style={{ marginTop: 12, fontSize: 14, color: '#444' }}>
              <strong>Resultado:</strong>{' '}
              {bulkProgress.filter(p => p.status === 'success').length} importado(s) com sucesso,{' '}
              {bulkProgress.filter(p => p.status === 'error').length} com erro.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          {bulkImportRunning ? (
            <Button
              label='Cancelar importação'
              onClick={() => { bulkAbortRef.current = true; }}
              style={{ color: '#c62828' }}
            />
          ) : (
            <Button label='Fechar' filled onClick={() => setOpenBulkProgressModal(false)} />
          )}
        </DialogActions>
      </Dialog>

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </>
  );
}
